/**
 * Bulk Operation Results Modal
 *
 * Displays summary after bulk operation completes.
 * Shows success count, failure count, and detailed error list.
 */

import { Modal, Space, Typography, Table, Alert, Button } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

const { Text, Title } = Typography

export interface FailedItem {
  uid: string
  name: string
  error: string
}

export interface BulkOperationResultsProps {
  /** Whether the modal is visible */
  visible: boolean

  /** Operation type (human-readable) */
  operation: string

  /** Total number of items attempted */
  total: number

  /** Number of items that succeeded */
  succeeded: number

  /** Number of items that failed */
  failed: number

  /** Array of failed items with error details */
  failedItems?: FailedItem[]

  /** Callback when user closes the modal */
  onClose: () => void

  // ========================================
  // SERVER METADATA (from API response meta)
  // ========================================

  /** Server-confirmed count of items requested (from meta.requested) */
  serverRequested?: number

  /** Whether counts are confirmed by server metadata */
  fromServerMeta?: boolean

  // ========================================
  // LIFECYCLE STATE TRANSITIONS
  // ========================================

  /** State before the action (for lifecycle operations) */
  previousState?: string

  /** State after the action (for lifecycle operations) */
  newState?: string

  /** Number of records superseded (for activate actions) */
  supersededCount?: number

  /** Number of continuations created (for activate actions) */
  continuationsCreated?: number
}

export function BulkOperationResults({
  visible,
  operation,
  total,
  succeeded,
  failed,
  failedItems = [],
  onClose,
  serverRequested: _serverRequested, // Available for server-requested count display
  fromServerMeta,
  previousState,
  newState,
  supersededCount,
  continuationsCreated,
}: BulkOperationResultsProps) {
  const hasFailures = failed > 0
  const isPartialSuccess = succeeded > 0 && failed > 0
  const isComplete = succeeded === total

  // Table columns for failed items
  const columns: ColumnsType<FailedItem> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string) => text || 'Unnamed',
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      render: (text: string) => <Text type="danger">{text}</Text>,
    },
  ]

  return (
    <Modal
      open={visible}
      title={`${operation} Results`}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={700}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Summary */}
        <Space direction="vertical" size="small">
          {isComplete && (
            <Space>
              <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
              <Title level={5} style={{ margin: 0 }}>
                Successfully {operation.toLowerCase()} {succeeded} of {total} offerings
              </Title>
            </Space>
          )}

          {isPartialSuccess && (
            <Space>
              <CloseCircleOutlined style={{ fontSize: 24, color: '#faad14' }} />
              <Title level={5} style={{ margin: 0 }}>
                Partial Success: {succeeded} succeeded, {failed} failed
              </Title>
            </Space>
          )}

          {!isComplete && !isPartialSuccess && (
            <Space>
              <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
              <Title level={5} style={{ margin: 0 }}>
                Operation failed: {failed} of {total} offerings could not be processed
              </Title>
            </Space>
          )}

          <Text type="secondary">
            Succeeded: {succeeded} | Failed: {failed} | Total: {total}
            {fromServerMeta && (
              <CheckCircleOutlined style={{ marginLeft: 8, color: '#52c41a' }} title="Confirmed by server" />
            )}
          </Text>

          {/* State transition info for lifecycle operations */}
          {previousState && newState && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              State change: {previousState} → {newState}
            </Text>
          )}

          {/* Supersession info for activate actions */}
          {typeof supersededCount === 'number' && supersededCount > 0 && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              {supersededCount} previous {supersededCount === 1 ? 'version was' : 'versions were'} superseded
            </Text>
          )}

          {/* Continuation info for activate actions */}
          {typeof continuationsCreated === 'number' && continuationsCreated > 0 && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              {continuationsCreated} continuation {continuationsCreated === 1 ? 'record' : 'records'} created
            </Text>
          )}
        </Space>

        {/* Alert for failures */}
        {hasFailures && (
          <Alert
            message={`${failed} ${failed === 1 ? 'item' : 'items'} failed to process`}
            description="Review the errors below and try again."
            type="warning"
            showIcon
          />
        )}

        {/* Failed Items Table */}
        {hasFailures && failedItems.length > 0 && (
          <div>
            <Title level={5}>Failed Items</Title>
            <Table
              columns={columns}
              dataSource={failedItems}
              rowKey="uid"
              pagination={false}
              size="small"
              scroll={{ y: 300 }}
            />
          </div>
        )}
      </Space>
    </Modal>
  )
}
